// Data constants and registries for catalog log events.
// Large lookup arrays/maps live here to keep catalog-log-event.ts focused on types.

import type {
  CatalogEventDiscriminator,
  DiscriminatorCategory,
  SpaceCategory,
} from '@/types/data/catalog-log-event';

// ---------------------------------------------------------------------------
// Discriminator constants
// ---------------------------------------------------------------------------

export const ALL_DISCRIMINATORS: CatalogEventDiscriminator[] = [
  'mesh://catalog/v1/events/access_denied',
  'mesh://catalog/v1/events/access_review_completed',
  'mesh://catalog/v1/events/account_created',
  'mesh://catalog/v1/events/account_deleted',
  'mesh://catalog/v1/events/account_disabled',
  'mesh://catalog/v1/events/account_display_name_changed',
  'mesh://catalog/v1/events/account_email_changed',
  'mesh://catalog/v1/events/account_enabled',
  'mesh://catalog/v1/events/account_locked',
  'mesh://catalog/v1/events/account_unlocked',
  'mesh://catalog/v1/events/administrative_unit_member_added',
  'mesh://catalog/v1/events/administrative_unit_member_removed',
  'mesh://catalog/v1/events/agent_created',
  'mesh://catalog/v1/events/agent_deleted',
  'mesh://catalog/v1/events/agent_permission_changed',
  'mesh://catalog/v1/events/agent_updated',
  'mesh://catalog/v1/events/baseline_updated',
  'mesh://catalog/v1/events/channel_created',
  'mesh://catalog/v1/events/channel_removed',
  'mesh://catalog/v1/events/compliance_check_completed',
  'mesh://catalog/v1/events/conditional_access_blocked',
  'mesh://catalog/v1/events/consent_granted',
  'mesh://catalog/v1/events/consent_requested',
  'mesh://catalog/v1/events/credential_added',
  'mesh://catalog/v1/events/credential_removed',
  'mesh://catalog/v1/events/device_compliance_changed',
  'mesh://catalog/v1/events/device_registered',
  'mesh://catalog/v1/events/device_removed',
  'mesh://catalog/v1/events/entity_patched',
  'mesh://catalog/v1/events/entity_removed',
  'mesh://catalog/v1/events/entity_retired',
  'mesh://catalog/v1/events/exception_granted',
  'mesh://catalog/v1/events/group_created',
  'mesh://catalog/v1/events/group_description_changed',
  'mesh://catalog/v1/events/group_member_added',
  'mesh://catalog/v1/events/group_member_removed',
  'mesh://catalog/v1/events/group_name_changed',
  'mesh://catalog/v1/events/group_removed',
  'mesh://catalog/v1/events/license_assigned',
  'mesh://catalog/v1/events/license_removed',
  'mesh://catalog/v1/events/login_failed',
  'mesh://catalog/v1/events/login_succeeded',
  'mesh://catalog/v1/events/mfa_challenge',
  'mesh://catalog/v1/events/mfa_failed',
  'mesh://catalog/v1/events/mfa_succeeded',
  'mesh://catalog/v1/events/multi_factor_registered',
  'mesh://catalog/v1/events/multi_factor_removed',
  'mesh://catalog/v1/events/multi_factor_updated',
  'mesh://catalog/v1/events/password_changed',
  'mesh://catalog/v1/events/password_reset',
  'mesh://catalog/v1/events/permission_granted',
  'mesh://catalog/v1/events/permission_revoked',
  'mesh://catalog/v1/events/policy_modified',
  'mesh://catalog/v1/events/policy_violation_detected',
  'mesh://catalog/v1/events/resource_accessed',
  'mesh://catalog/v1/events/risk_cleared',
  'mesh://catalog/v1/events/risk_detected',
  'mesh://catalog/v1/events/role_assigned',
  'mesh://catalog/v1/events/role_removed',
  'mesh://catalog/v1/events/security_alert',
  'mesh://catalog/v1/events/sensitive_data_accessed',
  'mesh://catalog/v1/events/session_created',
  'mesh://catalog/v1/events/session_terminated',
  'mesh://catalog/v1/events/suspicious_activity',
  'mesh://catalog/v1/events/ticket_approved',
  'mesh://catalog/v1/events/ticket_closed',
  'mesh://catalog/v1/events/ticket_created',
  'mesh://catalog/v1/events/ticket_rejected',
];

export const DISCRIMINATOR_ENTITY_PATCHED: CatalogEventDiscriminator =
  'mesh://catalog/v1/events/entity_patched';
export const DISCRIMINATOR_ENTITY_REMOVED: CatalogEventDiscriminator =
  'mesh://catalog/v1/events/entity_removed';
export const DISCRIMINATOR_ENTITY_RETIRED: CatalogEventDiscriminator =
  'mesh://catalog/v1/events/entity_retired';

// ---------------------------------------------------------------------------
// Discriminator categories — semantic groupings for the filter sidebar
// ---------------------------------------------------------------------------

export const DISCRIMINATOR_CATEGORIES: DiscriminatorCategory[] = [
  {
    label: 'Authentication',
    discriminators: [
      'mesh://catalog/v1/events/login_succeeded',
      'mesh://catalog/v1/events/login_failed',
      'mesh://catalog/v1/events/mfa_challenge',
      'mesh://catalog/v1/events/mfa_succeeded',
      'mesh://catalog/v1/events/mfa_failed',
      'mesh://catalog/v1/events/multi_factor_registered',
      'mesh://catalog/v1/events/multi_factor_updated',
      'mesh://catalog/v1/events/multi_factor_removed',
    ],
  },
  {
    label: 'Account Lifecycle',
    discriminators: [
      'mesh://catalog/v1/events/account_created',
      'mesh://catalog/v1/events/account_deleted',
      'mesh://catalog/v1/events/account_enabled',
      'mesh://catalog/v1/events/account_disabled',
      'mesh://catalog/v1/events/account_locked',
      'mesh://catalog/v1/events/account_unlocked',
      'mesh://catalog/v1/events/account_display_name_changed',
      'mesh://catalog/v1/events/account_email_changed',
    ],
  },
  {
    label: 'Credentials',
    discriminators: [
      'mesh://catalog/v1/events/password_changed',
      'mesh://catalog/v1/events/password_reset',
      'mesh://catalog/v1/events/credential_added',
      'mesh://catalog/v1/events/credential_removed',
    ],
  },
  {
    label: 'Access Control',
    discriminators: [
      'mesh://catalog/v1/events/permission_granted',
      'mesh://catalog/v1/events/permission_revoked',
      'mesh://catalog/v1/events/access_denied',
      'mesh://catalog/v1/events/resource_accessed',
      'mesh://catalog/v1/events/sensitive_data_accessed',
      'mesh://catalog/v1/events/conditional_access_blocked',
    ],
  },
  {
    label: 'Roles & Groups',
    discriminators: [
      'mesh://catalog/v1/events/role_assigned',
      'mesh://catalog/v1/events/role_removed',
      'mesh://catalog/v1/events/group_created',
      'mesh://catalog/v1/events/group_removed',
      'mesh://catalog/v1/events/group_name_changed',
      'mesh://catalog/v1/events/group_description_changed',
      'mesh://catalog/v1/events/group_member_added',
      'mesh://catalog/v1/events/group_member_removed',
    ],
  },
  {
    label: 'Security & Risk',
    discriminators: [
      'mesh://catalog/v1/events/risk_detected',
      'mesh://catalog/v1/events/risk_cleared',
      'mesh://catalog/v1/events/security_alert',
      'mesh://catalog/v1/events/suspicious_activity',
      'mesh://catalog/v1/events/access_review_completed',
    ],
  },
  {
    label: 'Agents',
    discriminators: [
      'mesh://catalog/v1/events/agent_created',
      'mesh://catalog/v1/events/agent_updated',
      'mesh://catalog/v1/events/agent_deleted',
      'mesh://catalog/v1/events/agent_permission_changed',
    ],
  },
  {
    label: 'Administrative Units',
    discriminators: [
      'mesh://catalog/v1/events/administrative_unit_member_added',
      'mesh://catalog/v1/events/administrative_unit_member_removed',
    ],
  },
  {
    label: 'Entity Changes',
    discriminators: [
      'mesh://catalog/v1/events/entity_patched',
      'mesh://catalog/v1/events/entity_removed',
      'mesh://catalog/v1/events/entity_retired',
    ],
  },
  {
    label: 'Policy & Compliance',
    discriminators: [
      'mesh://catalog/v1/events/policy_modified',
      'mesh://catalog/v1/events/policy_violation_detected',
      'mesh://catalog/v1/events/compliance_check_completed',
      'mesh://catalog/v1/events/baseline_updated',
    ],
  },
  {
    label: 'Sessions',
    discriminators: [
      'mesh://catalog/v1/events/session_created',
      'mesh://catalog/v1/events/session_terminated',
    ],
  },
  {
    label: 'Tickets',
    discriminators: [
      'mesh://catalog/v1/events/ticket_created',
      'mesh://catalog/v1/events/ticket_approved',
      'mesh://catalog/v1/events/ticket_rejected',
      'mesh://catalog/v1/events/ticket_closed',
    ],
  },
  {
    label: 'Licensing',
    discriminators: [
      'mesh://catalog/v1/events/license_assigned',
      'mesh://catalog/v1/events/license_removed',
    ],
  },
  {
    label: 'Devices',
    discriminators: [
      'mesh://catalog/v1/events/device_registered',
      'mesh://catalog/v1/events/device_removed',
      'mesh://catalog/v1/events/device_compliance_changed',
    ],
  },
  {
    label: 'Consent',
    discriminators: [
      'mesh://catalog/v1/events/consent_requested',
      'mesh://catalog/v1/events/consent_granted',
    ],
  },
  {
    label: 'Channels',
    discriminators: [
      'mesh://catalog/v1/events/channel_created',
      'mesh://catalog/v1/events/channel_removed',
    ],
  },
  {
    label: 'Exceptions',
    discriminators: ['mesh://catalog/v1/events/exception_granted'],
  },
];

// ---------------------------------------------------------------------------
// Spaces
// ---------------------------------------------------------------------------

export const LOG_SPACE_ACTIVITY = 'activity';

export const SPACE_CATEGORIES: SpaceCategory[] = [
  {
    label: 'Core Entities',
    items: [
      { value: 'accounts', label: 'Accounts' },
      { value: 'applications', label: 'Applications' },
      { value: 'devices', label: 'Devices' },
      { value: 'groups', label: 'Groups' },
      { value: 'licenses', label: 'Licenses' },
      { value: 'locations', label: 'Locations' },
      { value: 'persons', label: 'Persons' },
      { value: 'roles', label: 'Roles' },
      { value: 'tickets', label: 'Tickets' },
    ],
  },
  {
    label: 'Identity & Access',
    items: [
      { value: 'permissions', label: 'Permissions' },
      { value: 'policies', label: 'Policies' },
      { value: 'security_configurations', label: 'Security configurations' },
      { value: 'password_policies', label: 'Password policies' },
      { value: 'multi_factors', label: 'Multi-factor methods' },
    ],
  },
  {
    label: 'Organizational',
    items: [
      { value: 'organizational_units', label: 'Organizational units' },
      { value: 'employees', label: 'Employees' },
      { value: 'contacts', label: 'Contacts' },
      { value: 'compliance_baselines', label: 'Compliance baselines' },
    ],
  },
  {
    label: 'Account Relations',
    items: [
      { value: 'account_licenses', label: 'Account licenses' },
      { value: 'account_managers', label: 'Account managers' },
      { value: 'account_multi_factors', label: 'Account MFA' },
      { value: 'account_secrets', label: 'Account secrets' },
      { value: 'person_accounts', label: 'Person accounts' },
      { value: 'account_permissions', label: 'Account permissions' },
      { value: 'account_roles', label: 'Account roles' },
    ],
  },
  {
    label: 'Application Relations',
    items: [
      { value: 'application_accounts', label: 'Application accounts' },
      { value: 'application_groups', label: 'Application groups' },
      { value: 'application_permissions', label: 'Application permissions' },
      { value: 'application_roles', label: 'Application roles' },
    ],
  },
  {
    label: 'Device Relations',
    items: [
      { value: 'device_accounts', label: 'Device accounts' },
      { value: 'device_applications', label: 'Device applications' },
    ],
  },
  {
    label: 'Group Relations',
    items: [
      { value: 'group_licenses', label: 'Group licenses' },
      { value: 'group_members', label: 'Group members' },
      { value: 'group_permissions', label: 'Group permissions' },
      { value: 'group_roles', label: 'Group roles' },
    ],
  },
  {
    label: 'Misc',
    items: [
      { value: LOG_SPACE_ACTIVITY, label: 'Activity' },
      { value: 'network_objects', label: 'Network objects' },
      { value: 'channels', label: 'Channels' },
      { value: 'channel_accounts', label: 'Channel accounts' },
      { value: 'role_permissions', label: 'Role permissions' },
      { value: 'ticket_comments', label: 'Ticket comments' },
      { value: 'attributes', label: 'Attributes' },
    ],
  },
];

/** Singular display label for each well-known space value. */
export const SPACE_SINGULAR_LABEL: Record<string, string> = {
  // Core Entities
  accounts: 'Account',
  applications: 'Application',
  devices: 'Device',
  groups: 'Group',
  licenses: 'License',
  locations: 'Location',
  persons: 'Person',
  roles: 'Role',
  tickets: 'Ticket',
  // Identity & Access
  permissions: 'Permission',
  policies: 'Policy',
  security_configurations: 'Security configuration',
  password_policies: 'Password policy',
  multi_factors: 'Multi-factor method',
  // Organizational
  organizational_units: 'Organizational unit',
  employees: 'Employee',
  contacts: 'Contact',
  compliance_baselines: 'Compliance baseline',
  // Account Relations
  account_licenses: 'Account license',
  account_managers: 'Account manager',
  account_multi_factors: 'Account MFA',
  account_secrets: 'Account secret',
  person_accounts: 'Person account',
  account_permissions: 'Account permission',
  account_roles: 'Account role',
  // Application Relations
  application_accounts: 'Application account',
  application_groups: 'Application group',
  application_permissions: 'Application permission',
  application_roles: 'Application role',
  // Device Relations
  device_accounts: 'Device account',
  device_applications: 'Device application',
  // Group Relations
  group_licenses: 'Group license',
  group_members: 'Group member',
  group_permissions: 'Group permission',
  group_roles: 'Group role',
  // Misc
  [LOG_SPACE_ACTIVITY]: 'Activity',
  network_objects: 'Network object',
  channels: 'Channel',
  channel_accounts: 'Channel account',
  role_permissions: 'Role permission',
  ticket_comments: 'Ticket comment',
  attributes: 'Attribute',
};

/**
 * Returns a user-friendly singular label for a space value.
 * Falls back to title-casing the raw value if unknown.
 */
export function spaceLabel(space: string): string {
  return (
    SPACE_SINGULAR_LABEL[space] ??
    space.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

// Human-readable label for a discriminator
export function activityEventLabel(discriminator: string): string {
  const suffix = discriminator.split('/').pop() ?? discriminator;
  return suffix.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
